import { skipToken } from '@reduxjs/toolkit/query'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import isEmpty from 'lodash-es/isEmpty'
import { useCallback } from 'react'
import { useFetchUserStatusQuery } from '@/api/user-api'
import {
  useLazyFetchKolCollectionInfiniteQuery,
  useFetchKolCollectionsQuery,
} from '@/api/workspace-api/kol-collections-api'
import { useFetchCollectionOwnersQuery } from '@/api/workspaces-api'
import {
  type FetchKolCollectionsInfiniteRequest,
  type FetchKolCollectionsResponse,
} from '@/types/schema/api/kol-collections'

type UseCollectionData = (collectionId?: number) => {
  ownerList: ColumnFilterItem[]
  isFetchingCollections: boolean
  collections?: FetchKolCollectionsResponse
  isNoCollectionData: boolean
  fetchCollections:
    | ((
        params: FetchKolCollectionsInfiniteRequest,
      ) => ReturnType<typeof useLazyFetchKolCollectionInfiniteQuery>[0])
    | ((params: FetchKolCollectionsInfiniteRequest) => void)
}

const useCollectionData: UseCollectionData = () => {
  const { data: userStatus } = useFetchUserStatusQuery()

  const [
    fetchCollectionsFunc,
    { data: collections, isFetching: isFetchingCollections },
  ] = useLazyFetchKolCollectionInfiniteQuery()

  const { data: ownerList } = useFetchCollectionOwnersQuery(
    userStatus?.currentWorkspaceId
      ? { workspaceId: userStatus?.currentWorkspaceId }
      : skipToken,
  )

  const { data: initialCollectionList } = useFetchKolCollectionsQuery({
    page: 1,
  })

  const fetchCollections = useCallback(
    (params: FetchKolCollectionsInfiniteRequest) => {
      return fetchCollectionsFunc({
        ...params,
        owner_ids: isEmpty(params.owner_ids) ? undefined : params.owner_ids,
      })
    },
    [fetchCollectionsFunc],
  )

  const isNoCollectionData = initialCollectionList?.total === 0

  return {
    ownerList: ownerList
      ? ownerList.map((owner) => {
          return {
            text: owner.name,
            value: String(owner.id),
          }
        })
      : [],
    isFetchingCollections,
    collections,
    fetchCollections,
    isNoCollectionData,
  }
}

export default useCollectionData
